import { CSSProperties, useState } from 'react';

type DieType = 'd4' | 'd6' | 'd8' | 'd10' | 'd12' | 'd20';

interface Dice3DIconProps {
  type: DieType;
  size?: number;
  color?: string;
  style?: CSSProperties;
  usePng?: boolean; // Use PNG renders if available
}

/**
 * 3D polyhedral dice icons
 * Can use PNG renders from dice-box or fallback to SVG
 */
export function Dice3DIcon({ type, size = 48, color, style, usePng = true }: Dice3DIconProps) {
  const [pngError, setPngError] = useState(false);
  const baseColor = color || getDieColor(type);

  // Try PNG first if enabled and not errored
  if (usePng && !pngError && !color) {
    // Manual adjustments for imperfect crops
    // d20: shifted right 14px (crop was slightly off-center)
    const manualOffset = type === 'd20' ? { marginLeft: 14 } : {};

    return (
      <img
        src={`/assets/${type}.png`}
        alt={type.toUpperCase()}
        width={size}
        height={size}
        style={{
          objectFit: 'contain',
          ...manualOffset,
          ...style,
        }}
        onError={() => setPngError(true)}
      />
    );
  }

  // Generate unique IDs for gradients based on size to avoid conflicts
  const id = `${type}-${size}-${Math.random().toString(36).substr(2, 5)}`;

  const icons: Record<DieType, JSX.Element> = {
    // D4 - Tetrahedron
    d4: (
      <svg width={size} height={size} viewBox="0 0 48 48" style={style}>
        <defs>
          <linearGradient id={`${id}-a`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={lighten(baseColor, 50)} />
            <stop offset="50%" stopColor={baseColor} />
            <stop offset="100%" stopColor={darken(baseColor, 30)} />
          </linearGradient>
          <linearGradient id={`${id}-b`} x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={lighten(baseColor, 20)} />
            <stop offset="100%" stopColor={darken(baseColor, 40)} />
          </linearGradient>
          <linearGradient id={`${id}-c`} x1="50%" y1="0%" x2="50%" y2="100%">
            <stop offset="0%" stopColor={darken(baseColor, 10)} />
            <stop offset="100%" stopColor={darken(baseColor, 50)} />
          </linearGradient>
        </defs>
        <polygon points="24,5 7,42 24,34" fill={`url(#${id}-b)`} />
        <polygon points="24,5 41,42 24,34" fill={`url(#${id}-c)`} />
        <polygon points="7,42 41,42 24,34" fill={`url(#${id}-a)`} />
        <polygon points="24,5 7,42 41,42" fill="none" stroke={darken(baseColor, 60)} strokeWidth="1" strokeLinejoin="round"/>
      </svg>
    ),

    // D6 - Cube
    d6: (
      <svg width={size} height={size} viewBox="0 0 48 48" style={style}>
        <defs>
          <linearGradient id={`${id}-top`} x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={lighten(baseColor, 40)} />
            <stop offset="100%" stopColor={lighten(baseColor, 60)} />
          </linearGradient>
          <linearGradient id={`${id}-left`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={lighten(baseColor, 10)} />
            <stop offset="100%" stopColor={darken(baseColor, 30)} />
          </linearGradient>
          <linearGradient id={`${id}-right`} x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={darken(baseColor, 15)} />
            <stop offset="100%" stopColor={darken(baseColor, 45)} />
          </linearGradient>
        </defs>
        <polygon points="24,6 8,15 24,24 40,15" fill={`url(#${id}-top)`} />
        <polygon points="8,15 8,35 24,44 24,24" fill={`url(#${id}-left)`} />
        <polygon points="40,15 40,35 24,44 24,24" fill={`url(#${id}-right)`} />
        <polyline points="8,15 24,6 40,15 40,35 24,44 8,35 8,15" fill="none" stroke={darken(baseColor, 55)} strokeWidth="1" strokeLinejoin="round"/>
        <line x1="24" y1="24" x2="24" y2="44" stroke={darken(baseColor, 55)} strokeWidth="0.75"/>
      </svg>
    ),

    // D8 - Octahedron
    d8: (
      <svg width={size} height={size} viewBox="0 0 48 48" style={style}>
        <defs>
          <linearGradient id={`${id}-tl`} x1="100%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stopColor={baseColor} />
            <stop offset="100%" stopColor={lighten(baseColor, 50)} />
          </linearGradient>
          <linearGradient id={`${id}-tr`} x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={darken(baseColor, 20)} />
            <stop offset="100%" stopColor={lighten(baseColor, 20)} />
          </linearGradient>
          <linearGradient id={`${id}-bl`} x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={darken(baseColor, 10)} />
            <stop offset="100%" stopColor={darken(baseColor, 45)} />
          </linearGradient>
          <linearGradient id={`${id}-br`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={darken(baseColor, 30)} />
            <stop offset="100%" stopColor={darken(baseColor, 55)} />
          </linearGradient>
        </defs>
        <polygon points="24,4 5,24 24,24" fill={`url(#${id}-tl)`} />
        <polygon points="24,4 43,24 24,24" fill={`url(#${id}-tr)`} />
        <polygon points="5,24 24,44 24,24" fill={`url(#${id}-bl)`} />
        <polygon points="43,24 24,44 24,24" fill={`url(#${id}-br)`} />
        <polygon points="24,4 5,24 24,44 43,24" fill="none" stroke={darken(baseColor, 60)} strokeWidth="1" strokeLinejoin="round"/>
      </svg>
    ),

    // D10 - Pentagonal Trapezohedron (kite-shaped faces, elongated diamond)
    d10: (
      <svg width={size} height={size} viewBox="0 0 48 48" style={style}>
        <defs>
          <linearGradient id={`${id}-top`} x1="50%" y1="100%" x2="50%" y2="0%">
            <stop offset="0%" stopColor={lighten(baseColor, 20)} />
            <stop offset="100%" stopColor={lighten(baseColor, 55)} />
          </linearGradient>
          <linearGradient id={`${id}-tl`} x1="100%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stopColor={lighten(baseColor, 5)} />
            <stop offset="100%" stopColor={lighten(baseColor, 35)} />
          </linearGradient>
          <linearGradient id={`${id}-tr`} x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={darken(baseColor, 10)} />
            <stop offset="100%" stopColor={lighten(baseColor, 15)} />
          </linearGradient>
          <linearGradient id={`${id}-bl`} x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={darken(baseColor, 15)} />
            <stop offset="100%" stopColor={darken(baseColor, 45)} />
          </linearGradient>
          <linearGradient id={`${id}-br`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={darken(baseColor, 25)} />
            <stop offset="100%" stopColor={darken(baseColor, 55)} />
          </linearGradient>
        </defs>
        {/* Top kite face */}
        <polygon points="24,3 14,16 24,20 34,16" fill={`url(#${id}-top)`} />
        {/* Upper left kite */}
        <polygon points="14,16 6,24 14,34 24,20" fill={`url(#${id}-tl)`} />
        {/* Upper right kite */}
        <polygon points="34,16 42,24 34,34 24,20" fill={`url(#${id}-tr)`} />
        {/* Lower left kite */}
        <polygon points="14,34 6,24 24,45 24,20" fill={`url(#${id}-bl)`} />
        {/* Lower right kite */}
        <polygon points="34,34 42,24 24,45 24,20" fill={`url(#${id}-br)`} />
        {/* Outline */}
        <path d="M24,3 L14,16 L6,24 L14,34 L24,45 L34,34 L42,24 L34,16 Z" fill="none" stroke={darken(baseColor, 60)} strokeWidth="1" strokeLinejoin="round"/>
        {/* Inner edges */}
        <path d="M24,3 L24,20 M14,16 L24,20 L34,16 M6,24 L24,20 L42,24 M14,34 L24,20 L34,34" fill="none" stroke={darken(baseColor, 50)} strokeWidth="0.5" strokeLinejoin="round"/>
      </svg>
    ),

    // D12 - Dodecahedron (pentagonal faces - showing front pentagon with surrounding pentagons)
    d12: (
      <svg width={size} height={size} viewBox="0 0 48 48" style={style}>
        <defs>
          <linearGradient id={`${id}-center`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={lighten(baseColor, 50)} />
            <stop offset="100%" stopColor={lighten(baseColor, 15)} />
          </linearGradient>
          <linearGradient id={`${id}-top`} x1="50%" y1="100%" x2="50%" y2="0%">
            <stop offset="0%" stopColor={lighten(baseColor, 25)} />
            <stop offset="100%" stopColor={lighten(baseColor, 55)} />
          </linearGradient>
          <linearGradient id={`${id}-tl`} x1="100%" y1="50%" x2="0%" y2="50%">
            <stop offset="0%" stopColor={lighten(baseColor, 10)} />
            <stop offset="100%" stopColor={lighten(baseColor, 35)} />
          </linearGradient>
          <linearGradient id={`${id}-tr`} x1="0%" y1="50%" x2="100%" y2="50%">
            <stop offset="0%" stopColor={darken(baseColor, 5)} />
            <stop offset="100%" stopColor={lighten(baseColor, 20)} />
          </linearGradient>
          <linearGradient id={`${id}-bl`} x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={darken(baseColor, 20)} />
            <stop offset="100%" stopColor={darken(baseColor, 50)} />
          </linearGradient>
          <linearGradient id={`${id}-br`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={darken(baseColor, 30)} />
            <stop offset="100%" stopColor={darken(baseColor, 55)} />
          </linearGradient>
        </defs>
        {/* Center pentagon (front face) */}
        <polygon points="24,10 13,18 16,31 32,31 35,18" fill={`url(#${id}-center)`} />
        {/* Top pentagon */}
        <polygon points="24,10 13,18 8,10 24,3 40,10 35,18" fill={`url(#${id}-top)`} />
        {/* Top-left pentagon */}
        <polygon points="13,18 8,10 3,20 6,32 16,31" fill={`url(#${id}-tl)`} />
        {/* Top-right pentagon */}
        <polygon points="35,18 40,10 45,20 42,32 32,31" fill={`url(#${id}-tr)`} />
        {/* Bottom-left pentagon */}
        <polygon points="16,31 6,32 14,44 24,40" fill={`url(#${id}-bl)`} />
        {/* Bottom-right pentagon */}
        <polygon points="32,31 42,32 34,44 24,40" fill={`url(#${id}-br)`} />
        {/* Bottom center (partial) */}
        <polygon points="16,31 24,40 32,31" fill={darken(baseColor, 40)} />
        {/* Outline */}
        <path d="M24,3 L8,10 L3,20 L6,32 L14,44 L34,44 L42,32 L45,20 L40,10 L24,3" fill="none" stroke={darken(baseColor, 60)} strokeWidth="1" strokeLinejoin="round"/>
        {/* Inner edges */}
        <path d="M24,10 L13,18 L16,31 L32,31 L35,18 Z" fill="none" stroke={darken(baseColor, 55)} strokeWidth="0.75" strokeLinejoin="round"/>
        <path d="M24,3 L24,10 M8,10 L13,18 M40,10 L35,18 M6,32 L16,31 M42,32 L32,31 M16,31 L24,40 L32,31" fill="none" stroke={darken(baseColor, 50)} strokeWidth="0.5"/>
      </svg>
    ),

    // D20 - Icosahedron viewed from triangular face (classic d20 look)
    d20: (
      <svg width={size} height={size} viewBox="0 0 48 48" style={style}>
        <defs>
          <linearGradient id={`${id}-main`} x1="50%" y1="0%" x2="50%" y2="100%">
            <stop offset="0%" stopColor={lighten(baseColor, 50)} />
            <stop offset="50%" stopColor={lighten(baseColor, 20)} />
            <stop offset="100%" stopColor={baseColor} />
          </linearGradient>
          <linearGradient id={`${id}-left`} x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={lighten(baseColor, 20)} />
            <stop offset="100%" stopColor={darken(baseColor, 40)} />
          </linearGradient>
          <linearGradient id={`${id}-right`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={lighten(baseColor, 10)} />
            <stop offset="100%" stopColor={darken(baseColor, 35)} />
          </linearGradient>
          <linearGradient id={`${id}-bl`} x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={darken(baseColor, 20)} />
            <stop offset="100%" stopColor={darken(baseColor, 55)} />
          </linearGradient>
          <linearGradient id={`${id}-br`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={darken(baseColor, 30)} />
            <stop offset="100%" stopColor={darken(baseColor, 60)} />
          </linearGradient>
          <linearGradient id={`${id}-bottom`} x1="50%" y1="0%" x2="50%" y2="100%">
            <stop offset="0%" stopColor={darken(baseColor, 35)} />
            <stop offset="100%" stopColor={darken(baseColor, 55)} />
          </linearGradient>
        </defs>
        {/* Main front triangle - the iconic d20 face */}
        <polygon points="24,4 6,32 42,32" fill={`url(#${id}-main)`} />
        {/* Left side triangles */}
        <polygon points="24,4 6,32 3,18" fill={`url(#${id}-left)`} />
        <polygon points="6,32 3,18 10,42" fill={`url(#${id}-bl)`} />
        {/* Right side triangles */}
        <polygon points="24,4 42,32 45,18" fill={`url(#${id}-right)`} />
        <polygon points="42,32 45,18 38,42" fill={`url(#${id}-br)`} />
        {/* Bottom triangle */}
        <polygon points="6,32 42,32 24,44" fill={`url(#${id}-bottom)`} />
        {/* Bottom corners */}
        <polygon points="6,32 10,42 24,44" fill={darken(baseColor, 50)} />
        <polygon points="42,32 38,42 24,44" fill={darken(baseColor, 55)} />
        {/* Outline - hexagonal-ish shape */}
        <path d="M24,4 L3,18 L10,42 L38,42 L45,18 Z" fill="none" stroke={darken(baseColor, 65)} strokeWidth="1.25" strokeLinejoin="round"/>
        {/* Inner triangle edges */}
        <path d="M24,4 L6,32 L42,32 Z" fill="none" stroke={darken(baseColor, 50)} strokeWidth="0.75" strokeLinejoin="round"/>
        <path d="M6,32 L24,44 L42,32 M3,18 L6,32 M45,18 L42,32 M10,42 L24,44 L38,42" fill="none" stroke={darken(baseColor, 45)} strokeWidth="0.5"/>
      </svg>
    ),
  };

  return icons[type];
}

function getDieColor(type: DieType): string {
  // Rich metallic/gemstone colors
  const colors: Record<DieType, string> = {
    d4: '#d4a84b',   // Polished brass
    d6: '#cd7f32',   // Bronze
    d8: '#a8c4d9',   // Brushed steel
    d10: '#9b59b6',  // Amethyst
    d12: '#2ecc71',  // Emerald
    d20: '#f1c40f',  // Gold
  };
  return colors[type];
}

function lighten(color: string, percent: number): string {
  const num = parseInt(color.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, (num >> 16) + amt);
  const G = Math.min(255, ((num >> 8) & 0x00ff) + amt);
  const B = Math.min(255, (num & 0x0000ff) + amt);
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
}

function darken(color: string, percent: number): string {
  const num = parseInt(color.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, (num >> 16) - amt);
  const G = Math.max(0, ((num >> 8) & 0x00ff) - amt);
  const B = Math.max(0, (num & 0x0000ff) - amt);
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
}
